// Client Coolify v4 — la partie de `--provision` qui crée l'infra.
//
// Remplace les 4 ressources créées à la main dans l'UI (cf. docs/deploy.md §5-10)
// par des appels API : projet → environment → MariaDB → Redis → Service Compose
// WordPress → Application webapp.
//
// Base : `${COOLIFY_URL}/api/v1`, `Authorization: Bearer <id>|<secret>`.
// Le token doit porter les permissions `write` ET `deploy`.

import type { HttpMethod, Requester } from "./http"

/**
 * Hostname interne d'une base managée Coolify = son UUID.
 *
 * Vérifié dans le modèle amont (`StandaloneMariadb::internalDbUrl` →
 * `mysql://user:pass@{uuid}:3306/db`, idem `StandaloneRedis`). C'est ce qui nous
 * évite de relire `internal_db_url` : ce champ est dans `$hidden` et n'est
 * exposé qu'aux tokens `read:sensitive`, alors que l'UUID est renvoyé
 * directement par le POST de création. `docs/deploy.md` demandait jusqu'ici de
 * « relever le hostname interne réel » à la main.
 */
export function internalHostFor(databaseUuid: string): string {
	return databaseUuid
}

/**
 * Coolify valide `description` contre une liste blanche — lettres (Unicode),
 * chiffres, espaces et `- _ . , ! ? ( ) ' " + = * / @ &` — et rejette tout le
 * reste en 422. Le tiret cadratin de nos libellés en fait partie, comme
 * n'importe quelle ponctuation exotique venue du nom de projet, qui est saisi
 * par l'opérateur. On remplace plutôt que de supprimer : « WP Reactor — Acme »
 * doit rester lisible en « WP Reactor - Acme ».
 */
export function sanitizeDescription(description: string): string {
	return description.replace(/[^\p{L}\p{N} \-_.,!?()'"+=*/@&]/gu, "-").trim()
}

export interface CoolifyEnv {
	key: string
	value: string
	/** `true` → Coolify n'interprète ni `$` ni `{{ }}` dans la valeur. */
	is_literal?: boolean
}

interface UuidResponse {
	uuid: string
}

export interface CreateServiceInput {
	name: string
	projectUuid: string
	environmentUuid: string
	environmentName: string
	serverUuid: string
	/** Contenu du compose EN CLAIR — encodé en base64 ici, comme l'exige l'API. */
	dockerComposeRaw: string
	description?: string
}

export interface CreateDockerImageAppInput {
	name: string
	projectUuid: string
	environmentUuid: string
	environmentName: string
	serverUuid: string
	/** Ex. `ghcr.io/owner/repo/webapp`. */
	image: string
	imageTag: string
	/** Domaines publics, séparés par des virgules, schéma inclus. */
	domains: string
	/** Port écouté dans le conteneur. */
	portsExposes: string
	healthCheckPath: string
	postDeploymentCommand?: string
	description?: string
}

export class CoolifyClient {
	private readonly base: string

	constructor(
		baseUrl: string,
		private readonly token: string,
		private readonly http: Requester,
	) {
		this.base = `${baseUrl.replace(/\/+$/, "")}/api/v1`
	}

	private call<T>(
		method: HttpMethod,
		path: string,
		label: string,
		body?: unknown,
		dryRunResult?: unknown,
	): Promise<T> {
		return this.http.request<T>({
			method,
			url: `${this.base}${path}`,
			label,
			body,
			dryRunResult,
			headers: { authorization: `Bearer ${this.token}` },
		})
	}

	/**
	 * Résout un serveur depuis un UUID ou un nom lisible.
	 *
	 * Renvoie aussi son IP, qui est la cible des enregistrements DNS — ça évite
	 * de la demander à l'utilisateur alors que Coolify la connaît déjà.
	 *
	 * En dry-run aucune requête n'est émise, donc la résolution par nom est
	 * impossible : on renvoie l'entrée telle quelle plutôt que d'échouer.
	 */
	async resolveServer(
		nameOrUuid: string,
	): Promise<{ uuid: string; ip: string }> {
		if (this.http.isDryRun) return { uuid: nameOrUuid, ip: "<ip-du-serveur>" }

		const servers = await this.call<
			{ uuid: string; name: string; ip: string }[]
		>("GET", "/servers", "Lister les serveurs")
		const match = servers.find(
			(s) => s.uuid === nameOrUuid || s.name === nameOrUuid,
		)
		if (!match) {
			const known = servers.map((s) => `${s.name} (${s.uuid})`).join(", ")
			throw new Error(
				`Serveur Coolify introuvable : "${nameOrUuid}". Serveurs disponibles : ${known || "aucun"}.`,
			)
		}
		return { uuid: match.uuid, ip: match.ip }
	}

	/**
	 * Crée le projet, en refusant de se greffer sur un homonyme.
	 *
	 * L'API rend un projet existant plutôt que de rejeter le doublon de nom. Sans
	 * ce garde-fou, un run relancé après un échec reprendrait le projet laissé par
	 * le précédent : ses ressources s'empileraient dedans, et la création de
	 * l'environment « production » échouerait en 409 sur celui déjà présent.
	 */
	/** UUID du projet portant ce nom, `undefined` s'il n'y en a pas. */
	async findProject(name: string): Promise<string | undefined> {
		if (this.http.isDryRun) return undefined
		const projects = await this.call<{ uuid: string; name: string }[]>(
			"GET",
			"/projects",
			"Lister les projets",
		)
		return projects.find((project) => project.name === name)?.uuid
	}

	async createProject(name: string, description: string): Promise<string> {
		const existing = await this.findProject(name)
		if (existing) {
			throw new Error(
				`Un projet Coolify « ${name} » existe déjà (${existing}). ` +
					"Supprime-le depuis l'UI Coolify, ou change WPR_PROJECT_NAME — " +
					"--provision refuse de se greffer sur un projet existant.",
			)
		}

		const res = await this.call<UuidResponse>(
			"POST",
			"/projects",
			`Créer le projet « ${name} »`,
			{ name, description: sanitizeDescription(description) },
			{ uuid: "dry-run-project-uuid" },
		)
		return res.uuid
	}

	/**
	 * Rend l'environment `name` du projet, en le créant s'il manque.
	 *
	 * Coolify provisionne un environment « production » à la création du projet :
	 * le poster sans regarder échoue en 409, y compris sur un projet qui vient
	 * d'être créé. On lit donc le projet d'abord.
	 */
	async resolveEnvironment(projectUuid: string, name: string): Promise<string> {
		if (this.http.isDryRun) return "dry-run-environment-uuid"

		const project = await this.call<{
			environments?: { uuid: string; name: string }[]
		}>("GET", `/projects/${projectUuid}`, `Lire le projet ${projectUuid}`)
		const existing = project.environments?.find((env) => env.name === name)
		if (existing) return existing.uuid

		const res = await this.call<UuidResponse>(
			"POST",
			`/projects/${projectUuid}/environments`,
			`Créer l'environment « ${name} »`,
			{ name },
			{ uuid: "dry-run-environment-uuid" },
		)
		return res.uuid
	}

	async createMariadb(input: {
		name: string
		projectUuid: string
		environmentUuid: string
		environmentName: string
		serverUuid: string
		database: string
		user: string
		password: string
		rootPassword: string
	}): Promise<string> {
		const res = await this.call<UuidResponse>(
			"POST",
			"/databases/mariadb",
			`Créer la base MariaDB « ${input.name} »`,
			{
				name: input.name,
				project_uuid: input.projectUuid,
				environment_uuid: input.environmentUuid,
				environment_name: input.environmentName,
				server_uuid: input.serverUuid,
				mariadb_database: input.database,
				mariadb_user: input.user,
				mariadb_password: input.password,
				mariadb_root_password: input.rootPassword,
				// La DB doit tourner AVANT le premier boot de WordPress : les
				// variables MARIADB_* ne sont honorées que sur un datadir vide,
				// donc on la déploie tout de suite, contrairement aux ressources
				// qui dépendent d'images GHCR pas encore construites.
				instant_deploy: true,
			},
			{ uuid: "dry-run-mariadb-uuid" },
		)
		return res.uuid
	}

	async createRedis(input: {
		name: string
		projectUuid: string
		environmentUuid: string
		environmentName: string
		serverUuid: string
		password: string
	}): Promise<string> {
		const res = await this.call<UuidResponse>(
			"POST",
			"/databases/redis",
			`Créer le cache Redis « ${input.name} »`,
			{
				name: input.name,
				project_uuid: input.projectUuid,
				environment_uuid: input.environmentUuid,
				environment_name: input.environmentName,
				server_uuid: input.serverUuid,
				redis_password: input.password,
				instant_deploy: true,
			},
			{ uuid: "dry-run-redis-uuid" },
		)
		return res.uuid
	}

	/** Ressource « Docker Compose » — le compose est transmis en base64. */
	async createService(input: CreateServiceInput): Promise<string> {
		const res = await this.call<UuidResponse>(
			"POST",
			"/services",
			`Créer le service Compose « ${input.name} »`,
			{
				name: input.name,
				description: sanitizeDescription(input.description ?? ""),
				project_uuid: input.projectUuid,
				environment_uuid: input.environmentUuid,
				environment_name: input.environmentName,
				server_uuid: input.serverUuid,
				docker_compose_raw: Buffer.from(
					input.dockerComposeRaw,
					"utf8",
				).toString("base64"),
				// L'image WordPress n'existe pas encore sur GHCR : c'est le premier
				// run de la CI qui la pousse, puis déclenche le deploy lui-même.
				instant_deploy: false,
				// Les labels Traefik du compose lisent `${WP_HOST}` / `${APP_HOST}`
				// depuis l'env de la ressource. L'échappement de Coolify (actif par
				// défaut) neutraliserait ces `$` et les hosts ne seraient jamais
				// résolus — d'où la désactivation.
				is_container_label_escape_enabled: false,
			},
			{ uuid: "dry-run-service-uuid" },
		)
		return res.uuid
	}

	/** Application depuis une image de registry — garde le rolling update natif. */
	async createDockerImageApplication(
		input: CreateDockerImageAppInput,
	): Promise<string> {
		const res = await this.call<UuidResponse>(
			"POST",
			"/applications/dockerimage",
			`Créer l'application « ${input.name} »`,
			{
				name: input.name,
				description: sanitizeDescription(input.description ?? ""),
				project_uuid: input.projectUuid,
				environment_uuid: input.environmentUuid,
				environment_name: input.environmentName,
				server_uuid: input.serverUuid,
				docker_registry_image_name: input.image,
				docker_registry_image_tag: input.imageTag,
				domains: input.domains,
				ports_exposes: input.portsExposes,
				// Sans health check, Coolify bascule le trafic avant que le serveur
				// SSR n'écoute — c'est lui qui rend le rolling update réellement
				// zero-downtime.
				health_check_enabled: true,
				health_check_path: input.healthCheckPath,
				health_check_port: input.portsExposes,
				health_check_method: "GET",
				health_check_scheme: "http",
				health_check_return_code: 200,
				health_check_interval: 10,
				health_check_timeout: 5,
				health_check_retries: 5,
				health_check_start_period: 15,
				post_deployment_command: input.postDeploymentCommand,
				connect_to_docker_network: true,
				is_force_https_enabled: true,
				instant_deploy: false,
			},
			{ uuid: "dry-run-application-uuid" },
		)
		return res.uuid
	}

	/**
	 * Pose les labels Traefik exacts d'une application, en second appel.
	 *
	 * Pourquoi pas dans le POST de création : Coolify écrase `custom_labels` par
	 * ses labels auto-générés dès que `is_container_label_readonly_enabled` est
	 * vrai — c'est le défaut, et ce réglage n'est PAS exposé par l'API. La même
	 * régénération sur `PATCH` est conditionnée à la présence de `domains` dans
	 * la requête ; un PATCH portant `custom_labels` SEUL passe donc à travers.
	 * D'où cet appel séparé, sans `domains`, après la création.
	 */
	async setApplicationLabels(
		applicationUuid: string,
		labels: string,
	): Promise<void> {
		await this.call(
			"PATCH",
			`/applications/${applicationUuid}`,
			"Poser les labels Traefik de la webapp",
			{
				custom_labels: Buffer.from(labels, "utf8").toString("base64"),
				is_container_label_escape_enabled: false,
			},
			{},
		)
	}

	async setEnvs(
		kind: "services" | "applications",
		uuid: string,
		envs: CoolifyEnv[],
	): Promise<void> {
		await this.call(
			"PATCH",
			`/${kind}/${uuid}/envs/bulk`,
			`Poser ${envs.length} variables d'environnement (${kind})`,
			{ data: envs.map((e) => ({ is_literal: true, ...e })) },
			[],
		)
	}

	async deploy(uuids: string[]): Promise<void> {
		await this.call(
			"POST",
			`/deploy?uuid=${uuids.join(",")}&force=true`,
			`Déployer ${uuids.length} ressource(s)`,
			undefined,
			{ deployments: [] },
		)
	}
}

/**
 * Labels Traefik de la webapp — source de vérité unique.
 *
 * Ils étaient jusqu'ici collés à la main dans l'UI Coolify depuis
 * `docs/traefik-labels.md` ; ce doc est désormais le miroir de cette constante.
 * `slug` et `appHost` sont substitués ici, donc les labels émis ne contiennent
 * plus aucune variable à interpoler.
 *
 * `priority=1` maintient ce routeur SOUS le routeur `wp-seo` du compose
 * WordPress, qui sert `/robots.txt` et les sitemaps sur ce même host.
 */
export function webappTraefikLabels(slug: string, appHost: string): string {
	return [
		"traefik.enable=true",
		"traefik.docker.network=coolify",
		`traefik.http.services.${slug}-webapp.loadbalancer.server.port=3000`,
		`traefik.http.middlewares.${slug}-webapp-compress.compress=true`,
		`traefik.http.middlewares.${slug}-webapp-compress.compress.encodings=br,gzip`,
		`traefik.http.middlewares.${slug}-webapp-compress.compress.defaultEncoding=gzip`,
		`traefik.http.routers.${slug}-webapp.rule=Host(\`${appHost}\`)`,
		`traefik.http.routers.${slug}-webapp.entrypoints=https`,
		`traefik.http.routers.${slug}-webapp.priority=1`,
		`traefik.http.routers.${slug}-webapp.middlewares=${slug}-webapp-compress`,
		`traefik.http.routers.${slug}-webapp.tls=true`,
		`traefik.http.routers.${slug}-webapp.tls.certresolver=letsencrypt`,
	].join("\n")
}
